import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import FAQAssist from '../FAQAssist';

const ForeignContactSEAD3Test: React.FC = () => {
  const [hasForeignContact, setHasForeignContact] = useState<string>('');
  const [clearanceLevel, setClearanceLevel] = useState<string>('');

  return (
    <div className="flex-1 p-8 bg-gray-50">
      <div className="max-w-4xl mx-auto">
        <Link to="/rag-test" className="inline-flex items-center text-blue-600 hover:text-blue-800 mb-4">
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to RAG Testing Dashboard
        </Link>

        <div className="bg-white rounded-lg shadow-sm p-8">
          <div className="mb-6">
            <div className="flex items-start justify-between mb-4">
              <h2 className="text-2xl font-bold text-gray-900">Foreign Contact Reporting Test (SEAD-3 Version)</h2>
              <span className="px-3 py-1 bg-purple-100 text-purple-800 text-xs font-medium rounded-full">
                SEAD-3 Policy
              </span>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-blue-800">
                <strong>Source Document:</strong> SEAD-3-Reporting-U.pdf, Section F.2 - Foreign Contacts (comprehensive policy document)
              </p>
              <p className="text-sm text-blue-800 mt-2">
                This version uses the authoritative SEAD-3 policy document covering all covered individuals'
                reporting requirements for foreign contacts and relationships.
              </p>
            </div>

            {/* Clearance Level Selector */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <label htmlFor="clearanceLevel" className="block text-sm font-medium text-green-900 mb-2">
                Clearance Level (Optional - filters requirements)
              </label>
              <select
                id="clearanceLevel"
                value={clearanceLevel}
                onChange={(e) => setClearanceLevel(e.target.value)}
                className="w-full md:w-64 px-3 py-2 border border-green-300 rounded-md text-sm focus:ring-green-500 focus:border-green-500"
              >
                <option value="">All Levels (No Filtering)</option>
                <option value="confidential">Confidential</option>
                <option value="secret">Secret</option>
                <option value="l">L (DOE)</option>
                <option value="top_secret">Top Secret</option>
                <option value="q">Q (DOE)</option>
                <option value="sci">SCI</option>
              </select>
              <p className="text-xs text-green-700 mt-2">
                SEAD-3 has different reporting requirements based on clearance level. Select a level to see tailored FAQs and assessments.
              </p>
            </div>
          </div>

          <div className="space-y-8">
            {/* Foreign Contact Question with FAQ */}
            <div>
              <div className="flex items-start justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 flex-1">
                  Do you have any foreign contacts that need to be reported?
                </h3>
                <FAQAssist
                  topic="foreign-contact-reporting-sead3"
                  clearanceLevel={clearanceLevel || undefined}
                />
              </div>

              <div className="space-y-2">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="hasForeignContact"
                    value="yes"
                    checked={hasForeignContact === 'yes'}
                    onChange={(e) => setHasForeignContact(e.target.value)}
                    className="h-4 w-4 text-gov-blue focus:ring-gov-blue border-gray-300"
                  />
                  <span className="ml-2 text-sm text-gray-700">Yes</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="hasForeignContact"
                    value="no"
                    checked={hasForeignContact === 'no'}
                    onChange={(e) => setHasForeignContact(e.target.value)}
                    className="h-4 w-4 text-gov-blue focus:ring-gov-blue border-gray-300"
                  />
                  <span className="ml-2 text-sm text-gray-700">No</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="hasForeignContact"
                    value="unsure"
                    checked={hasForeignContact === 'unsure'}
                    onChange={(e) => setHasForeignContact(e.target.value)}
                    className="h-4 w-4 text-gov-blue focus:ring-gov-blue border-gray-300"
                  />
                  <span className="ml-2 text-sm text-gray-700">I'm not sure</span>
                </label>
              </div>

              {/* Additional context for unsure selection */}
              {hasForeignContact === 'unsure' && (
                <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-start">
                    <svg className="w-5 h-5 text-amber-600 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div>
                      <h4 className="text-sm font-medium text-amber-900 mb-1">Need Help Determining Reporting Requirements?</h4>
                      <p className="text-sm text-amber-800">
                        Use the FAQ Assistant above to get guidance from SEAD-3 policy on what constitutes a reportable foreign contact,
                        or describe your specific situation for a personalized assessment.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* SEAD-3 Specific Guidance */}
            {hasForeignContact === 'yes' && (
              <div className="p-6 bg-gray-50 border border-gray-200 rounded-lg">
                <h4 className="text-sm font-medium text-gray-900 mb-3">SEAD-3 Section F.2 Key Reporting Criteria - Test with FAQ</h4>
                <ul className="text-sm text-gray-700 space-y-2">
                  <li className="flex items-start">
                    <span className="mr-2">•</span>
                    <span><strong>Unofficial contact</strong> with a known or suspected foreign intelligence entity</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2">•</span>
                    <span><strong>Continuing association</strong> with foreign nationals involving bonds of affection, obligation, or intimate contact</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2">•</span>
                    <span><strong>Exchange of personal information</strong> with foreign nationals</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2">•</span>
                    <span><strong>Re-occurring contact</strong> that extends beyond typical public interaction</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2">•</span>
                    <span><strong>Foreign national roommate</strong> for more than 30 days (applies to TS/Q holders)</span>
                  </li>
                </ul>
                <p className="text-xs text-gray-600 mt-3 italic">
                  Note: Limited or casual public contact with foreign nationals is not required absent other reporting requirements.
                </p>
              </div>
            )}
          </div>

          {/* Testing Instructions */}
          <div className="mt-8 p-4 bg-purple-50 border border-purple-200 rounded-lg">
            <h4 className="text-sm font-medium text-purple-900 mb-2">Testing Notes - SEAD-3 Version</h4>
            <ul className="text-sm text-purple-800 space-y-1">
              <li>• Compare responses to the Original version (decision-tree document)</li>
              <li>• Test complex scenarios: bonds of affection vs. casual contact</li>
              <li>• Ask about "continuing association" criteria</li>
              <li>• Test roommate scenarios and the 30-day threshold</li>
              <li>• Verify citations reference SEAD-3-Reporting-U.pdf pages</li>
              <li>• Use the comparison page to see both versions side-by-side</li>
            </ul>
          </div>

          {/* Link to Comparison */}
          <div className="mt-6">
            <Link
              to="/rag-test/comparison"
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
              </svg>
              Compare Both Versions Side-by-Side
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForeignContactSEAD3Test;
